import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import { useUIStore } from '@/store/uiStore';
import { marketingApi } from '@/services/api';
import { ArrowLeft, Loader2, Menu, Send } from 'lucide-react';
import toast from 'react-hot-toast';

const CAMPAIGN_TOKENS: Array<{ token: string; label: string }> = [
  { token: 'customer_name', label: 'Customer name' },
  { token: 'shop_name', label: 'Shop name' },
  { token: 'email', label: 'Email' },
  { token: 'phone', label: 'Phone' },
  { token: 'address', label: 'Address' },
  { token: 'city', label: 'City' },
  { token: 'postal_code', label: 'Postcode' },
];

export default function MarketingCampaignsPage() {
  const { toggleSidebar } = useUIStore();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState(
    'Hi {{customer_name}}, special offer from {{shop_name}}! Visit us soon.'
  );
  const [subject, setSubject] = useState('Offer from {{shop_name}}');
  const [customerType, setCustomerType] = useState('All');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [channels, setChannels] = useState<string[]>(['WhatsApp']);

  const insertBodyToken = (token: string) => {
    const insert = `{{${token}}}`;
    const el = bodyRef.current;
    if (!el) {
      setBody((b) => b + insert);
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + insert + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + insert.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const { data: channelData } = useQuery({
    queryKey: ['marketingChannels'],
    queryFn: async () => (await marketingApi.listChannels()).data.message,
  });

  const entitled = useMemo(() => {
    const e = channelData?.entitlements || {};
    return (['WhatsApp', 'SMS', 'Email'] as const).filter((ch) => e[ch] && channelData?.connected?.[ch]);
  }, [channelData]);

  const { data: campaigns, isLoading } = useQuery({
    queryKey: ['marketingCampaigns'],
    queryFn: async () => (await marketingApi.listCampaigns()).data.message as Array<Record<string, unknown>>,
  });

  const { data: detail } = useQuery({
    queryKey: ['marketingCampaign', selected],
    queryFn: async () => (await marketingApi.getCampaign(selected!)).data.message,
    enabled: Boolean(selected),
  });

  const previewMut = useMutation({
    mutationFn: () =>
      marketingApi.previewAudience({
        customer_type: customerType,
        city,
        postal_code: postalCode,
      }),
  });

  const saveMut = useMutation({
    mutationFn: () =>
      marketingApi.saveCampaign({
        title,
        body,
        subject,
        customer_type: customerType,
        city,
        postal_code: postalCode,
        channels,
      }),
    onSuccess: (res) => {
      toast.success('Campaign saved as draft');
      qc.invalidateQueries({ queryKey: ['marketingCampaigns'] });
      const name = res.data.message?.name;
      if (name) setSelected(name);
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Save failed';
      toast.error(String(msg));
    },
  });

  const sendMut = useMutation({
    mutationFn: async () => {
      let name = selected;
      if (!name) {
        const saved = await saveMut.mutateAsync();
        name = saved.data.message?.name;
      }
      return marketingApi.sendCampaign(name!);
    },
    onSuccess: () => {
      toast.success('Campaign queued');
      qc.invalidateQueries({ queryKey: ['marketingCampaigns'] });
      qc.invalidateQueries({ queryKey: ['marketingCampaign'] });
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Send failed';
      toast.error(String(msg));
    },
  });

  const toggleChannel = (ch: string) => {
    setChannels((prev) => (prev.includes(ch) ? prev.filter((x) => x !== ch) : [...prev, ch]));
  };

  const previewCount = previewMut.data?.data?.message?.count;

  return (
    <div className="flex h-screen w-full max-w-[100%] overflow-hidden bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <TrialBanner />
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 shrink-0">
          <button type="button" onClick={toggleSidebar} className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100">
            <Menu className="w-5 h-5" />
          </button>
          <Link to="/settings?tab=marketing" className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Marketing
          </Link>
          <h1 className="text-lg font-semibold text-gray-900">Campaigns</h1>
          <Link
            to="/marketing/usage"
            className="ml-auto text-sm text-teal-700 hover:underline"
          >
            Message usage
          </Link>
        </header>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 max-w-3xl">
            Promotional messages may require consent (UK PECR). Only customers with mobile/email are
            included. Merge fields: {'{{customer_name}}'}, {'{{shop_name}}'}.
          </p>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 space-y-3">
              <h2 className="font-semibold text-gray-900">New campaign</h2>
              <label className="block text-sm">
                <span className="font-medium text-gray-700">Title</span>
                <input
                  className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <label className="text-sm">
                  <span className="font-medium text-gray-700">Customer type</span>
                  <select
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    value={customerType}
                    onChange={(e) => setCustomerType(e.target.value)}
                  >
                    <option value="All">All</option>
                    <option value="Retailer">Retailer</option>
                    <option value="Wholesale">Wholesale</option>
                    <option value="Distributor">Distributor</option>
                  </select>
                </label>
                <label className="text-sm">
                  <span className="font-medium text-gray-700">City</span>
                  <input
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                </label>
                <label className="text-sm">
                  <span className="font-medium text-gray-700">Postcode</span>
                  <input
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    placeholder="SW1A"
                  />
                </label>
              </div>

              <div className="flex flex-wrap gap-3 text-sm">
                {(['WhatsApp', 'SMS', 'Email'] as const).map((ch) => {
                  const ok = entitled.includes(ch);
                  return (
                    <label
                      key={ch}
                      className={`inline-flex items-center gap-1.5 ${ok ? '' : 'opacity-40'}`}
                    >
                      <input
                        type="checkbox"
                        disabled={!ok}
                        checked={channels.includes(ch)}
                        onChange={() => toggleChannel(ch)}
                      />
                      {ch}
                      {!ok ? ' (locked)' : ''}
                    </label>
                  );
                })}
              </div>

              <label className="block text-sm">
                <span className="font-medium text-gray-700">Email subject</span>
                <input
                  className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </label>
              <div className="space-y-1.5">
                <span className="text-sm font-medium text-gray-700">Message</span>
                <div className="flex flex-wrap gap-1.5">
                  {CAMPAIGN_TOKENS.map((t) => (
                    <button
                      key={t.token}
                      type="button"
                      className="text-[11px] px-2 py-1 rounded-md border border-gray-200 bg-gray-50 hover:bg-teal-50 hover:border-teal-300 text-gray-700"
                      onClick={() => insertBodyToken(t.token)}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <textarea
                  ref={bodyRef}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm min-h-[100px]"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="btn-secondary text-sm"
                  disabled={previewMut.isPending}
                  onClick={() => previewMut.mutate()}
                >
                  Preview audience
                </button>
                {previewCount != null && (
                  <span className="text-sm text-gray-600">{previewCount} customers</span>
                )}
                <button
                  type="button"
                  className="btn-secondary text-sm"
                  disabled={saveMut.isPending}
                  onClick={() => saveMut.mutate()}
                >
                  Save draft
                </button>
                <button
                  type="button"
                  className="btn-primary text-sm inline-flex items-center gap-1.5"
                  disabled={sendMut.isPending || channels.length === 0}
                  onClick={() => {
                    if (
                      !window.confirm(
                        `Send this campaign to matching customers on ${channels.join(', ')}?`
                      )
                    )
                      return;
                    sendMut.mutate();
                  }}
                >
                  {sendMut.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Send
                </button>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 space-y-3">
              <h2 className="font-semibold text-gray-900">Recent campaigns</h2>
              {isLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : !campaigns?.length ? (
                <p className="text-sm text-gray-500">No campaigns yet.</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {campaigns.map((c) => (
                    <li key={String(c.name)}>
                      <button
                        type="button"
                        className={`w-full text-left py-2.5 px-1 hover:bg-gray-50 rounded ${
                          selected === c.name ? 'bg-sky-50' : ''
                        }`}
                        onClick={() => setSelected(String(c.name))}
                      >
                        <div className="flex justify-between gap-2">
                          <span className="font-medium text-gray-900 text-sm truncate">
                            {String(c.title)}
                          </span>
                          <span className="text-xs text-gray-500 shrink-0">{String(c.status)}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {String(c.channels)} · audience {String(c.audience_count || 0)} · sent{' '}
                          {String(c.sent_count || 0)}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {detail && (
                <div className="border-t border-gray-100 pt-3 space-y-2">
                  <h3 className="text-sm font-semibold text-gray-900">Recipient log</h3>
                  <div className="max-h-64 overflow-y-auto text-xs">
                    {(detail.recipients || []).length === 0 ? (
                      <p className="text-gray-500">No recipients logged yet.</p>
                    ) : (
                      <table className="w-full text-left">
                        <thead>
                          <tr className="text-gray-500">
                            <th className="py-1 pr-2">Channel</th>
                            <th className="py-1 pr-2">Address</th>
                            <th className="py-1">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.recipients.map(
                            (r: {
                              name: string;
                              channel: string;
                              address: string;
                              status: string;
                            }) => (
                              <tr key={r.name} className="border-t border-gray-50">
                                <td className="py-1 pr-2">{r.channel}</td>
                                <td className="py-1 pr-2 truncate max-w-[140px]">{r.address}</td>
                                <td className="py-1">{r.status}</td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
